<script lang="ts" setup>
// ================================
// IMPORTS
// ================================
import { computed, ref } from "vue"

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        /**
         * Label de la ligne (ex: Lundi)
         */
        label: string
        /**
         * Valeur de la ligne (ex: 8h - 12h)
         */
        value?: string
        /**
         * Identifiant du collapse (optionnel, active le mode cliquable)
         */
        collapseId?: string
        /**
         * Classe(s) CSS personnalisée(s) appliquée(s) au label
         */
        labelClass?: string
    }>(),
    {
        value: "",
        collapseId: "",
        labelClass: "",
    }
)

// ================================
// ÉTAT
// ================================
/** État ouvert/fermé du collapse */
const isOpen = ref(false)

// ================================
// DONNÉES CALCULÉES
// ================================
/** Icône chevron selon l'état ouvert/fermé */
const getChevronIcon = computed(() =>
    isOpen.value ? "pi pi-chevron-up" : "pi pi-chevron-down"
)

// ================================
// INTERACTIONS
// ================================
/**
 * Gère le clic sur l'en-tête pour toggler le collapse
 */
function onClick(): void {
    if (props.collapseId) {
        isOpen.value = !isOpen.value
    }
}
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT -->
    <!-- ================================ -->
    <div
        class="usage-profile-item-field flex flex-col justify-center min-h-11 p-3 transition-colors duration-300"
        :class="{
            'cursor-pointer hover:bg-primary-100': collapseId,
            'bg-primary-100': isOpen && collapseId,
        }"
        :data-clickable="!!collapseId"
        :data-open="isOpen"
    >
        <div
            class="flex flex-row justify-between items-center flex-wrap"
            @click.prevent.stop="onClick"
        >
            <span
                class="usage-profile-item-field__label usage-profile-item-field__value text-sm text-surface-700"
                :class="labelClass"
                >{{ label ?? "-" }}</span
            >
            <div class="flex items-center gap-4">
                <span
                    v-if="value"
                    class="usage-profile-item-field__value text-sm text-surface-700"
                >
                    {{ value ?? "-" }}
                </span>
                <slot />
                <BaseIcon
                    v-if="collapseId"
                    :data-active="isOpen"
                    :icon="getChevronIcon"
                    size="14px"
                />
            </div>
        </div>
        <div v-if="collapseId" :id="collapseId" v-show="isOpen">
            <slot name="collapse" />
        </div>
    </div>
</template>
